import { E2eRunSummary } from '../../api/types';

/** Only 'passed' is green; 'skipped' is neutral; anything else (failed,
 * error, timedout, interrupted, …) means the run needs a look. */
export function runKind(run: E2eRunSummary): 'passed' | 'skipped' | 'failed' {
  if (run.status === 'passed') return 'passed';
  if (run.status === 'skipped') return 'skipped';
  return 'failed';
}

// Same categories and order as the run-time Slack notification's Result
// field (N passed / N failed / N skipped, zero categories omitted) — no
// invented denominator: "194/308 passed" reads as a 63% pass rate when the
// other 114 are deliberate profile skips. Flaky is deliberately not shown:
// for a deploy go/no-go it is the same as passed (retries eventually
// succeeded), and the Slack Result doesn't show it either.
export function runSummaryText(run: E2eRunSummary): string {
  if (run.total === 0) {
    // No testcases at all (e.g. the run itself errored before producing
    // results) — show the mart status as-is.
    return run.status;
  }
  const parts = [`${run.passed} passed`];
  if (run.failed > 0) {
    parts.push(`${run.failed} failed`);
  }
  if (run.skipped > 0) {
    parts.push(`${run.skipped} skipped`);
  }
  return parts.join(', ');
}

export type E2eCellState =
  /** Every run green (or neutral skip) — a plain ✓ cell, no detail block. */
  | { kind: 'ok' }
  /** At least one run needs a look; label goes into the red cell. */
  | { kind: 'failed'; label: string }
  /** No run in the lookback window for this product × env. */
  | { kind: 'none' };

export function cellState(runs: E2eRunSummary[]): E2eCellState {
  if (runs.length === 0) {
    return { kind: 'none' };
  }
  const bad = runs.filter(run => runKind(run) === 'failed');
  if (bad.length === 0) {
    return { kind: 'ok' };
  }
  const failed = bad.reduce((sum, run) => sum + run.failed, 0);
  // A non-passed run can carry zero failed testcases (the run itself errored
  // before producing results) — say so instead of "0 failed".
  return { kind: 'failed', label: failed > 0 ? `${failed} failed` : 'error' };
}

/**
 * One-line rollup for the green runs inside a failure detail block, e.g.
 * "2 runs passed (edge)" — the failing runs are listed individually, the
 * rest don't deserve a line each.
 */
export function passedRollupText(
  okRuns: E2eRunSummary[],
  total: number,
): string {
  const n = okRuns.length;
  const runs = n === 1 ? 'run' : 'runs';
  if (n === total) {
    return `all ${n} ${runs} passed`;
  }
  const browsers = [...new Set(okRuns.map(run => run.browserName))].join(', ');
  return `${n} ${runs} passed${browsers ? ` (${browsers})` : ''}`;
}

/** Latest startedAt among the runs (ISO), for the rollup line's timestamp. */
export function latestStartedAt(runs: E2eRunSummary[]): string | null {
  let latest: string | null = null;
  for (const run of runs) {
    if (run.startedAt && (!latest || run.startedAt > latest)) {
      latest = run.startedAt;
    }
  }
  return latest;
}
